import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/*
 * DevModePanel — single floating "DEV" pill + side panel for role
 * impersonation and one-click QA bypass.
 *
 * Visibility rules:
 *   • Pill renders whenever `ENABLE_DEV_MODE=true` on the backend
 *     (returned by /api/dev/status). Anonymous visitors see it too so
 *     QA can hop into either portal in one click. In production with
 *     ENABLE_DEV_MODE unset, /api/dev/status returns false and the
 *     pill never renders.
 *
 * UX restraint:
 *   • Only ONE pill is ever on screen. Quick-login affordances live
 *     inside the side panel rather than competing for the bottom-left
 *     corner. Click pill → side panel opens → choose Client or Advisor
 *     bypass (or pick a specific user from the impersonation pickers).
 *
 * Security: every impersonation + quick-login still goes through the
 * backend's existing RBAC; this is a convenience UI, not an auth bypass.
 */
export default function DevModePanel() {
  const { user, checkSession } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/dev/status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus, user?.id, user?.impersonated_by]);

  // Frontend host-guard: never render the DEV pill on non-preview hosts,
  // even if /api/dev/status mistakenly returns dev_mode_enabled=true. The
  // backend has the same guard — this is defence in depth.
  const isPreviewHost = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname || '';
    return (
      h === 'localhost' ||
      h === '127.0.0.1' ||
      /\.preview\.emergentagent\.com$/.test(h)
    );
  }, []);

  // Pill is visible whenever dev mode is on AND we're on a safe dev host.
  const visible = useMemo(
    () => isPreviewHost && Boolean(status?.dev_mode_enabled),
    [isPreviewHost, status]
  );

  // Quick-login bypass — used by both anonymous and authenticated states.
  // /api/dev/quick-login is idempotent and always returns a valid
  // impersonation cookie for the chosen role.
  const quickLogin = useCallback(async (role) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/dev/quick-login`,
        { role },
        { withCredentials: true },
      );
      await checkSession();
      await refreshStatus();
      setOpen(false);
      navigate(role === 'advisor' ? '/portal/advisor' : '/portal/client');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[dev quick-login]', e?.response?.data || e);
      alert('Dev quick-login failed: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  }, [checkSession, refreshStatus, navigate]);

  // Load picker data the first time the panel opens, AND only when we
  // have a session that can call the developer-protected endpoints.
  useEffect(() => {
    if (!open || !visible) return;
    if (!status?.is_developer) return;
    let cancelled = false;
    (async () => {
      try {
        const [cs, as_] = await Promise.all([
          axios.get(`${API}/api/dev/clients?limit=100`, { withCredentials: true }),
          axios.get(`${API}/api/dev/advisors`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setClients(cs.data || []);
        setAdvisors(as_.data || []);
      } catch (_) { /* swallow — panel just shows empty state */ }
    })();
    return () => { cancelled = true; };
  }, [open, visible, status?.is_developer]);

  const filteredClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      (c.email || '').toLowerCase().includes(q)
      || (c.name || '').toLowerCase().includes(q));
  }, [clients, query]);

  const impersonate = async (target_user_id, landingPath) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/dev/impersonate`,
        { target_user_id },
        { withCredentials: true },
      );
      await checkSession();
      await refreshStatus();
      setOpen(false);
      if (landingPath) navigate(landingPath);
    } finally {
      setBusy(false);
    }
  };

  const stopImpersonating = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/dev/stop`, {}, { withCredentials: true });
      await checkSession();
      await refreshStatus();
      navigate('/portal/advisor');
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  const isImpersonating = status?.is_impersonating;
  const imp = status?.impersonating_user;
  // Display label for the pill — prefers the user's actual identity
  // (Antonia@Verault.Travels for the owner record, the client's name
  // when impersonating, etc.) and falls back to a generic state when
  // we don't yet have a session.
  const identityName = (u) => (u?.name || u?.email || '').trim();
  let pillLabel = 'Enter portal';
  if (isImpersonating) {
    pillLabel = imp?.role === 'client'
      ? `Client View · ${identityName(imp)}`
      : `Advisor View · ${identityName(imp)}`;
  } else if (user) {
    pillLabel = user.role === 'client'
      ? `Client View · ${identityName(user)}`
      : `Advisor View · ${identityName(user)}`;
  }

  return (
    <>
      {/* Single floating pill. Bottom-LEFT — away from the Emergent
          "Made with" badge in the bottom-right of preview deployments. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="dev-mode-pill"
        className="fixed bottom-5 left-5 z-[9999] flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium shadow-lg backdrop-blur-md transition-all hover:scale-105 max-w-[280px]"
        style={{
          background: isImpersonating ? 'rgba(220, 38, 38, 0.92)' : 'rgba(26, 26, 26, 0.92)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.15)',
          letterSpacing: '0.04em',
        }}
        aria-label="Developer mode panel"
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: isImpersonating ? '#fef08a' : '#22c55e' }}
        />
        <span className="uppercase tracking-wider">DEV</span>
        <span className="opacity-80">·</span>
        <span className="font-normal truncate">{pillLabel}</span>
      </button>

      {/* Slide-in panel */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="dev-mode-backdrop"
          />
          <aside
            className="fixed bottom-20 left-5 z-[9999] w-[360px] max-h-[80vh] overflow-hidden rounded-xl shadow-2xl"
            style={{
              background: '#FAFAF8',
              border: '1px solid rgba(0,0,0,0.08)',
            }}
            data-testid="dev-mode-panel"
          >
            <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#999]">Developer Mode</p>
                <p className="font-serif text-base text-[#1A1A1A] truncate max-w-[260px]">{pillLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[#999] hover:text-[#1A1A1A] text-lg leading-none"
                aria-label="Close panel"
              >
                ×
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(80vh-60px)]">
              {/* Quick-login section — always rendered when not impersonating.
                  Lets QA hop into either portal with one click, regardless of
                  whether they're currently anonymous or auto-signed-in as
                  the owner. */}
              {!isImpersonating && (
                <div className="px-4 pt-4 pb-3 border-b border-black/5">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#999] mb-2">Quick Login Bypass</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => quickLogin('client')}
                      data-testid="dev-quicklogin-client"
                      className="flex-1 px-3 py-2 rounded text-xs font-medium bg-[#1A1A1A] text-white hover:bg-[#000] disabled:opacity-50 transition-colors"
                    >
                      Enter as Client
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => quickLogin('advisor')}
                      data-testid="dev-quicklogin-advisor"
                      className="flex-1 px-3 py-2 rounded text-xs font-medium bg-[#1A1A1A] text-white hover:bg-[#000] disabled:opacity-50 transition-colors"
                    >
                      Enter as Advisor
                    </button>
                  </div>
                  <p className="text-[10px] text-[#999] leading-relaxed mt-2">
                    One-click QA bypass. Each option opens the corresponding portal as the canonical seed account.
                  </p>
                </div>
              )}

              {isImpersonating && (
                <div className="px-4 py-3 bg-red-50 border-b border-red-100">
                  <p className="text-xs text-red-800 mb-2">
                    Impersonating <strong>{imp?.email}</strong> ({imp?.role})
                  </p>
                  <button
                    type="button"
                    onClick={stopImpersonating}
                    disabled={busy}
                    data-testid="dev-stop-impersonating"
                    className="w-full px-3 py-2 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Stop impersonating · return to developer
                  </button>
                </div>
              )}

              {/* Advisor / Client pickers — only rendered when the underlying
                  session has developer privileges (i.e. /dev/clients +
                  /dev/advisors will succeed). Anonymous QA uses Quick Login
                  above and doesn't need the full picker. */}
              {status?.is_developer && (
                <>
                  {/* Advisor / Owner picker */}
                  <div className="px-4 pt-4">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#999] mb-2">View as Advisor</p>
                    <div className="space-y-1.5">
                      {advisors.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => impersonate(a.id, '/portal/advisor')}
                          disabled={busy || a.id === user?.id || a.id === imp?.id}
                          data-testid={`dev-advisor-${a.email}`}
                          className="w-full text-left px-3 py-2 rounded text-xs hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <span className="text-[#1A1A1A]">{a.name || a.email}</span>
                          {a.id === user?.id && !isImpersonating && <span className="ml-2 text-[10px] text-[#999]">(you)</span>}
                          {a.id === imp?.id && <span className="ml-2 text-[10px] text-red-700">(current)</span>}
                        </button>
                      ))}
                      {advisors.length === 0 && <p className="text-xs text-[#999] py-2">Loading…</p>}
                    </div>
                  </div>

                  {/* Client picker */}
                  <div className="px-4 py-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#999]">View as Client</p>
                      <span className="text-[10px] text-[#999]">{filteredClients.length}</span>
                    </div>
                    <input
                      type="text"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="Search by name or email…"
                      data-testid="dev-client-search"
                      className="w-full px-3 py-2 text-xs rounded border border-black/10 bg-white focus:outline-none focus:border-black/30 mb-2"
                    />
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {filteredClients.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => impersonate(c.id, '/portal/client')}
                          disabled={busy || c.id === imp?.id}
                          data-testid={`dev-client-${c.email}`}
                          className="w-full text-left px-3 py-2 rounded text-xs hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[#1A1A1A]">{c.name || c.email}</span>
                            {c.is_demo && <span className="text-[9px] uppercase tracking-wider text-[#999] bg-[#F0EDE6] px-1.5 py-0.5 rounded">demo</span>}
                          </div>
                          <p className="text-[10px] text-[#999] mt-0.5">{c.email}</p>
                          {c.id === imp?.id && <p className="text-[10px] text-red-700 mt-0.5">(currently impersonating)</p>}
                        </button>
                      ))}
                      {filteredClients.length === 0 && (
                        <p className="text-xs text-[#999] py-3 text-center">No clients match</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="px-4 pb-4 pt-2">
                <p className="text-[10px] text-[#999] leading-relaxed">
                  All impersonation actions are audit-logged. RBAC remains enforced — your view
                  reflects the target user's true permissions.
                </p>
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
